import { computed, ref } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineOpportunitiesAPI from 'dashboard/api/pipeline/opportunities';
import {
  formatEditableNumber,
  formatLocalizedCurrency,
  formatLocalizedDateInTimezone,
  resolveReportingTimezone,
} from '../utils/localeFormatting';

const PAGE_SIZE = 50;

// Limite REAL da coluna, não um número escolhido aqui: `PipelineOpportunity`
// valida `void_reason` com `length: { maximum: 255 }`. Sem este teto na tela,
// 256 caracteres passavam pelo controller, quebravam no modelo e voltavam como
// 422 — que a UI mostrava como o erro genérico de estorno, sem dizer que o
// problema era o tamanho. O número mora aqui porque as DUAS telas de estorno
// (modal do board e aba do card) precisam do mesmo teto.
export const VOID_REASON_MAX_LENGTH = 255;

/**
 * Histórico financeiro de um card (Fase 59), compartilhado pelas DUAS
 * superfícies que o exibem: o modal aberto pelo board e a aba dentro do card.
 *
 * Elas nasceram com cópias separadas da mesma carga paginada, validação de
 * payload, invalidação de requisição e formatação — e as cópias já tinham
 * começado a divergir: cada correção (data no idioma da conta, falha de
 * paginação preservando a página carregada) precisou ser feita duas vezes, e a
 * segunda quase ficou para trás. Aqui a regra existe uma vez só.
 *
 * @param {import('vue').Ref<Object>} card - ref do card exibido
 * @returns {Object} estado e helpers do histórico
 */
export function useOpportunityHistory(card) {
  const { t, locale } = useI18n();
  const store = useStore();

  const opportunities = ref([]);
  const summary = ref(null);
  const loading = ref(false);
  const loadingMore = ref(false);
  // ⚠️ Sinalizador, NÃO a string traduzida: `t()` dentro do catch congela o
  // texto no idioma daquele instante, e o locale da conta só é aplicado depois
  // do boot — uma falha na primeira carga ficava em inglês para sempre num app
  // em pt-BR. Traduzir no template mantém a mensagem reativa.
  const loadFailed = ref(false);
  const appendFailed = ref(false);

  // Cada carga invalida a anterior: sem isso, trocar de card rápido deixaria a
  // lista do card antigo pintada no novo.
  let requestId = 0;

  const currencyCode = computed(
    () => summary.value?.currency || card.value?.currency || 'BRL'
  );

  const formatMoney = value => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    try {
      return formatLocalizedCurrency(number, {
        currency: currencyCode.value,
        locale: locale.value,
      });
    } catch {
      return `${currencyCode.value} ${number}`;
    }
  };

  // Data no fuso da CONTA e no idioma da conta. No fuso do navegador, uma venda
  // perto da meia-noite cairia no dia financeiro errado; sem locale (o que
  // `formatInReportingTimezone` faz) sairia "04 Aug 2026" numa tela em pt-BR.
  const formatDate = value =>
    formatLocalizedDateInTimezone(value, {
      locale: locale.value,
      timeZone: resolveReportingTimezone(store),
      dateStyle: 'medium',
    });

  // Contagens vêm do SERVIDOR: derivá-las da página faria "N estornadas"
  // discordar do total assim que o histórico passasse de uma página.
  const recordedCount = computed(() => Number(summary.value?.count ?? 0));
  const voidedCount = computed(() => Number(summary.value?.voided_count ?? 0));
  const historyCount = computed(() =>
    Number(
      summary.value?.history_count ?? recordedCount.value + voidedCount.value
    )
  );

  // Quantas linhas a LISTA tem, segundo a própria resposta que a paginou.
  // O resumo vem dos contadores denormalizados do card, que podem estar
  // atrasados: confiar neles esconderia o "carregar mais" e truncaria a trilha
  // de auditoria — ou ofereceria o botão para sempre.
  const listedCount = ref(null);
  const hasMore = computed(() => {
    const total = listedCount.value ?? historyCount.value;
    return opportunities.value.length < Number(total || 0);
  });
  const totalValue = computed(() => summary.value?.total ?? 0);
  const averageTicket = computed(() => {
    if (!recordedCount.value) return 0;
    return Number(totalValue.value) / recordedCount.value;
  });

  const isEmpty = computed(
    () =>
      !loading.value && !loadFailed.value && opportunities.value.length === 0
  );

  // Um 200 com shape inesperado NÃO pode virar "histórico vazio, total zero":
  // isso afirmaria que o cliente nunca comprou. Payload malformado é falha de
  // carga, igual a uma rejeição de rede.
  const isValidPayload = data =>
    Array.isArray(data?.opportunities) &&
    data?.summary !== null &&
    typeof data?.summary === 'object' &&
    data.summary.total !== undefined &&
    data.summary.count !== undefined;

  const load = async ({ append = false } = {}) => {
    requestId += 1;
    const currentRequest = requestId;
    if (append) loadingMore.value = true;
    else loading.value = true;
    // Falha ao paginar não derruba o que já está na tela: apagar a primeira
    // página levaria junto o total financeiro já conferido.
    if (append) appendFailed.value = false;
    else loadFailed.value = false;
    try {
      const { data } = await PipelineOpportunitiesAPI.list(card.value.id, {
        perPage: PAGE_SIZE,
        offset: append ? opportunities.value.length : 0,
      });
      if (currentRequest !== requestId) return;
      if (!isValidPayload(data)) {
        if (append) appendFailed.value = true;
        else loadFailed.value = true;
        return;
      }
      opportunities.value = append
        ? [...opportunities.value, ...data.opportunities]
        : data.opportunities;
      summary.value = data.summary;
      const total = Number(data.meta?.total_count);
      listedCount.value = Number.isFinite(total) ? total : null;
    } catch {
      if (currentRequest !== requestId) return;
      if (append) appendFailed.value = true;
      else loadFailed.value = true;
    } finally {
      // `return` dentro de finally engole exceções; o guard vira condição.
      if (currentRequest === requestId) {
        loading.value = false;
        loadingMore.value = false;
      }
    }
  };

  const loadMore = () => load({ append: true });

  // Estorno mora aqui, junto da carga: quem estorna precisa que a lista e o
  // total voltem do SERVIDOR (o acumulado é recalculado lá), não de um ajuste
  // otimista no cliente que poderia divergir do razão.
  const voiding = ref(null);
  const voidFailed = ref(null);

  // Guarda de QUAL linha o estorno falhou, não um booleano global: cancelar e
  // abrir outra venda mostrava o erro da anterior antes de qualquer requisição.
  // Permissão e o passo-a-passo da confirmação vivem AQUI, não em cada tela: o
  // modal do board e a aba do card mostram o mesmo histórico, e duplicar o
  // recorte de autorização é como as duas divergem sem ninguém notar.
  const { checkPermissions } = usePolicy();
  const canVoid = computed(() =>
    checkPermissions(['administrator', 'pipeline_manage'])
  );

  const voidingEntry = ref(null);
  const voidReason = ref('');

  // Motivo é OBRIGATÓRIO. Estorno tira dinheiro da receita, é irreversível, e
  // sem motivo a trilha de auditoria vira "Estornada: sem motivo informado" —
  // que não explica nada para quem abrir o histórico depois. A regra vive aqui,
  // não em cada tela: o modal do board e a aba do card exercitam o MESMO
  // estorno, e duplicar a validação é como as duas divergem sem ninguém notar.
  const voidReasonTrimmed = computed(() => voidReason.value.trim());
  const canConfirmVoid = computed(
    () =>
      voidReasonTrimmed.value.length > 0 &&
      voidReasonTrimmed.value.length <= VOID_REASON_MAX_LENGTH
  );

  const canVoidEntry = entry =>
    canVoid.value &&
    entry.status !== 'voided' &&
    voidingEntry.value !== entry.id;

  const startVoid = entry => {
    voidingEntry.value = entry.id;
    voidReason.value = '';
    voidFailed.value = null;
  };

  const cancelVoid = () => {
    voidingEntry.value = null;
    voidReason.value = '';
    voidFailed.value = null;
  };

  const voidSale = async (entry, reason) => {
    voiding.value = entry.id;
    voidFailed.value = null;
    try {
      await PipelineOpportunitiesAPI.void(entry.id, { reason });
    } catch {
      voidFailed.value = entry.id;
      voiding.value = null;
      return false;
    }

    // Recarga em bloco SEPARADO: o estorno já foi commitado e é irreversível.
    // Juntar os dois num try só fazia uma falha de rede na releitura ser
    // relatada como "não foi possível estornar" — e o operador tentaria de
    // novo uma operação que já aconteceu.
    try {
      await load();
    } catch {
      loadFailed.value = true;
    } finally {
      // Só agora: liberar antes da releitura reabriria a confirmação com os
      // dados velhos ainda na tela.
      voiding.value = null;
    }
    return true;
  };

  const confirmVoid = async entry => {
    // Guarda aqui além do botão desabilitado: o botão é a barreira que o
    // operador vê, esta é a que vale. Mandar string vazia fazia o servidor
    // aceitar o estorno sem motivo nenhum (POST com corpo `{}`, 200).
    if (!canConfirmVoid.value) return false;

    const done = await voidSale(entry, voidReasonTrimmed.value);
    if (done) cancelVoid();
    return done;
  };

  const reset = () => {
    opportunities.value = [];
    summary.value = null;
    listedCount.value = null;
    voiding.value = null;
    voidFailed.value = null;
    // Zera também os erros: sem isto, uma falha de "carregar mais" num card
    // continuaria pintada ao abrir OUTRO card, com um botão "tentar de novo"
    // que não se refere a nada do que está na tela.
    loadFailed.value = false;
    appendFailed.value = false;
  };

  // Duas ausências de nome que PARECEM a mesma e não são — a distinção é o
  // `source` do registro:
  //
  // * `backfill`: venda MIGRADA de antes do razão existir. O backfill grava
  //   título NULO em vez de fixar um idioma no banco, e ali "sem nome" é a
  //   verdade — ninguém sabe o que foi vendido.
  // * qualquer outro (`manual`/`api`/`automation`): fechamento AO VIVO de um
  //   card sem título. O nome não se perdeu, ele nunca existiu, e o que
  //   aconteceu é conhecido: o negócio foi ganho. Chamar isso de "venda sem
  //   nome" faz o histórico parecer defeituoso justamente no caso normal.
  //
  // Antes do razão virar core (28/08) só o primeiro caso chegava aqui, porque a
  // gravação ao vivo dependia da cross feature. Ao tornar o razão universal, o
  // segundo caso passou a usar um rótulo escrito para o primeiro.
  const entryTitle = entry => {
    if (entry.title) return entry.title;

    return entry.source === 'backfill'
      ? t('PIPELINE.OPPORTUNITIES.UNTITLED_SALE')
      : t('PIPELINE.OPPORTUNITIES.WON_SALE');
  };

  // Motivo gerado pelo SISTEMA vem como token (`system:...`), não como texto
  // traduzido: `void_reason` é coluna de auditoria imutável, e gravar a string
  // já traduzida congelava nela o idioma do request que por acaso disparou o
  // ajuste — inglês numa instalação em pt-BR quando o caminho era job ou
  // automação. Quem lê traduz.
  //
  // Motivo digitado pelo usuário NÃO casa com nenhum token e passa direto, igual
  // às linhas antigas, que já têm o texto congelado e continuam exibindo o que
  // está salvo.
  const traduzirMotivo = motivo => {
    if (!motivo?.startsWith('system:')) return motivo;

    const chave = motivo.slice('system:'.length).toUpperCase();
    const i18nKey = `PIPELINE.OPPORTUNITIES.VOID_REASONS.SYSTEM_${chave}`;
    const traduzido = t(i18nKey);
    // Token desconhecido (versão futura, rollback) não vira chave crua na tela.
    return traduzido === i18nKey ? motivo : traduzido;
  };

  // Estorno mexe em dinheiro: quem estornou e quando são parte da trilha, não
  // detalhe opcional.
  const voidLine = entry => {
    const reason =
      traduzirMotivo(entry.void_reason) ||
      t('PIPELINE.OPPORTUNITIES.NO_REASON');
    const parts = [t('PIPELINE.OPPORTUNITIES.VOIDED_WITH_REASON', { reason })];
    if (entry.voided_by_name) parts.push(entry.voided_by_name);
    const when = formatDate(entry.voided_at);
    if (when) parts.push(when);
    return parts.join(' · ');
  };

  // As LINHAS da venda (Fase 59/F5). Uma venda pode ter N itens; o cabeçalho
  // guarda só o total, e sem mostrar as linhas o histórico diz "R$ 8.891,00" sem
  // dizer de quê — que é justamente o que a venda com N produtos existe para
  // registrar.
  //
  // O rótulo é SEMPRE `title`: é a convenção que as linhas gravadas seguem, com
  // produto de catálogo (que grava o nome do produto ali) e sem produto (texto
  // livre). O payload de leitura devolve `pipeline_product_id`, não o nome do
  // produto, então tentar rotular pelo produto deixaria a linha de catálogo sem
  // nome — e as duas espécies convivem na mesma venda.
  const entryItems = entry => (Array.isArray(entry.items) ? entry.items : []);

  // Item sem nome usa o MESMO rótulo do cabeçalho sem nome, por decisão de spec
  // (21/08): uma regra só para "sem nome" nesta tela. O backfill grava
  // `title: ''` quando a venda de origem não tinha título — string VAZIA, não
  // nula —, e `'' || fallback` já resolve os dois casos sem um segundo `if`.
  // Sem isto a linha aparecia em branco, que é pior que um rótulo genérico:
  // some da tela um item que existe e tem valor.
  const itemLabel = item =>
    item.title || t('PIPELINE.OPPORTUNITIES.UNTITLED_SALE');

  // "3 × R$ 297,00" — a conta que explica o total da linha, mostrada só quando
  // ela explica ALGUMA coisa. Com quantidade 1 o unitário é igual ao total da
  // linha, que já está impresso ao lado: repetir o mesmo número duas vezes na
  // mesma linha não informa, atrapalha (visto em chatdev).
  const itemMeta = item => {
    const quantity = Number(item.quantity);
    const unit = formatMoney(item.unit_value);
    if (!Number.isFinite(quantity) || quantity === 1 || !unit) return '';
    // `formatLocalizedNumber` e não `String`: 3.000 não é "3" em toda locale.
    const quantidade = formatEditableNumber(quantity, locale.value).replace(
      /[.,]00$/,
      ''
    );
    return `${quantidade} × ${unit}`;
  };

  // Autor + ciclo. Backfill não tem autor (não sabe quem vendeu), então a linha
  // simplesmente não o menciona em vez de inventar um nome.
  const rowMeta = entry => {
    const parts = [];
    if (entry.recorded_by_name) parts.push(entry.recorded_by_name);
    if (entry.cycle_number) {
      parts.push(
        t('PIPELINE.OPPORTUNITIES.CYCLE', { number: entry.cycle_number })
      );
    }
    return parts.join(' · ');
  };

  return {
    opportunities,
    summary,
    loading,
    loadingMore,
    loadFailed,
    appendFailed,
    currencyCode,
    recordedCount,
    voidedCount,
    historyCount,
    hasMore,
    totalValue,
    averageTicket,
    isEmpty,
    formatMoney,
    formatDate,
    entryTitle,
    entryItems,
    itemLabel,
    itemMeta,
    voidLine,
    rowMeta,
    load,
    loadMore,
    reset,
    voidSale,
    voiding,
    voidFailed,
    canVoidEntry,
    voidingEntry,
    voidReason,
    canConfirmVoid,
    voidReasonMaxLength: VOID_REASON_MAX_LENGTH,
    startVoid,
    cancelVoid,
    confirmVoid,
  };
}
